'use client';

import { useEffect, useState } from 'react';

import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';
import { useBottomNavClearance } from '@ps/functions/hooks/useBottomNavReserve';
import { useCurrentUserId } from '@ps/ui/contexts/auth';

import { CommunityMessageProvider } from '../../contexts/community-message/provider';
import { useCommunityMessageContext } from '../../contexts/community-message/useCommunityMessageContext';
import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import { resolveMessageInteractionMode } from '../../functions/message/resolveMessageInteractionMode';
import { useMyChannelMembership } from '../../hooks/useMyChannelMembership';
import {
  usePinnedMessages,
  useRefreshPinnedMessages,
} from '../../hooks/usePinnedMessages';
import type { CommunityChannel } from '../../types/CommunityChannel';
import { ChannelHeader } from '../channel-detail/ChannelHeader';
import { ChannelPinnedBanner } from '../channel-detail/ChannelPinnedBanner';
import { JoinChannelBanner } from '../channel-detail/JoinChannelBanner';
import { ChannelComposer } from '../channel-message-editor/ChannelComposer';
import { ChannelMessageList } from '../channel-message-view/ChannelMessageList';
import { ChannelPinnedMessagesPane } from '../channel-pinned/ChannelPinnedMessagesPane';

interface Props {
  slug: string;
}

/**
 * Right-pane channel view — header + pinned banner + message list +
 * composer. Wraps the inner pane in a `CommunityMessageProvider`
 * scoped to this channel; the inner consumer reads `messages$` /
 * `sendMessage` / `loadMore` / `resendMessage` from context.
 *
 * When the pinned banner is activated the message list + composer
 * area is replaced by the pinned-messages list; the header stays so
 * the user keeps their context. Closing the pinned panel returns to
 * the normal message stream.
 */
export function ChannelDetailPane({ slug }: Props) {
  const { channelsState$ } = useCommunityContext();
  const [channelsState] = useBehaviorSubject(channelsState$);
  const channel = channelsState.channels.find(c => c.slug === slug);

  if (!channel) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <span className="text-content-4 text-sm">
          {channelsState.isLoading ? 'Loading channel…' : 'Channel not found.'}
        </span>
      </div>
    );
  }

  return (
    <CommunityMessageProvider channelId={channel.id}>
      <ChannelDetailPaneInner channel={channel} />
    </CommunityMessageProvider>
  );
}

function ChannelDetailPaneInner({ channel }: { channel: CommunityChannel }) {
  const currentUserId = useCurrentUserId();
  const { isOwner, creatorId } = useCommunityContext();
  const { sendMessage, pinnedMessagesInvalidated$ } =
    useCommunityMessageContext();
  const [showPinned, setShowPinned] = useState(false);
  const membership = useMyChannelMembership(channel.id);
  // The composer is the bottom-most element of this pane, and the fixed mobile
  // tab bar overlays it — fully in the Android app, where the bar's
  // `env(safe-area-inset-bottom)` reads 0 so nothing at all was reserved.
  // `ChannelListMasterPane` already clears the bar with a hardcoded
  // `3.75rem + env(...)`; this measures it instead so it tracks the real bar.
  const bottomNavClearance = useBottomNavClearance();

  // The pinned banner count comes from a dedicated pinned-only query, NOT the
  // main `messages$` stream: that stream is anchored on the newest seed and
  // never re-delivers pin edits to older messages, so a count derived from it
  // would go stale the moment an older message is pinned/un-pinned. Refetch
  // whenever a pin flips or a pinned message is deleted.
  const pinnedState$ = usePinnedMessages(creatorId, channel.id);
  const [pinnedState] = useBehaviorSubject(pinnedState$);
  const refreshPinned = useRefreshPinnedMessages(
    pinnedState$,
    creatorId,
    channel.id,
  );
  useEffect(() => {
    const sub = pinnedMessagesInvalidated$.subscribe(
      () => void refreshPinned(),
    );
    return () => sub.unsubscribe();
  }, [pinnedMessagesInvalidated$, refreshPinned]);

  const pinnedCount = pinnedState.messages.length;

  // 1:1 with PopshopRN's `CommunityChannelView` gating: the owner always posts;
  // a non-owner posts only once they've joined (and not in a broadcast channel).
  // A non-owner who hasn't joined (`membership === null`) sees the join banner
  // in the composer's place. `undefined` = still loading — render neither to
  // avoid flashing the banner at an actual member.
  const isMember = membership != null;
  const canCompose = isOwner || (isMember && !channel.isBroadcastChannel);
  const showJoinBanner = !isOwner && membership === null;

  // Message-action rights (reply / react) for this viewer — a non-member is
  // read-only until they join, matching PopshopRN's per-message mode.
  const interactionMode = resolveMessageInteractionMode({
    isOwner,
    isBroadcastChannel: channel.isBroadcastChannel,
    isMember,
  });

  if (showPinned) {
    return (
      <section className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
        <ChannelHeader channel={channel} withBackButton />
        <ChannelPinnedMessagesPane
          channel={channel}
          mode={interactionMode}
          onClose={() => setShowPinned(false)}
        />
      </section>
    );
  }

  return (
    <section className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
      <ChannelHeader channel={channel} withBackButton />
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <ChannelMessageList
          currentUserId={currentUserId}
          channelSlug={channel.slug}
          mode={interactionMode}
          hasPinnedBanner={pinnedCount > 0}
        />
        {/* Banner sits ON TOP of the message list so its
            `backdrop-blur` picks up the message content scrolling
            underneath — matches PopshopRN's translucent banner look.
            The list keeps its full scroll area; the banner just
            covers the top ~44px while remaining click-through where
            it has no content. */}
        <ChannelPinnedBanner
          count={pinnedCount}
          onClick={() => setShowPinned(true)}
        />
        {/* Floated over the message list bottom (matches upstream), not in the
            composer's slot. `pointer-events-none` on the host lets clicks pass
            through to the messages; the pill re-enables them for its button. */}
        {showJoinBanner ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-8 flex justify-center px-4">
            <JoinChannelBanner channel={channel} />
          </div>
        ) : null}
      </div>
      {canCompose ? (
        <ChannelComposer
          onSend={sendMessage}
          channelId={channel.id}
          bottomInset={bottomNavClearance}
        />
      ) : null}
    </section>
  );
}
